"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.getTransactions = void 0;
const network_1 = __importDefault(require("@ledgerhq/live-network/network"));
const chunk_1 = __importDefault(require("lodash/chunk"));
const range_1 = __importDefault(require("lodash/range"));
const env_1 = require("../../../env");
const constants_1 = require("../constants");
const logic_1 = require("../logic");
const types_1 = require("../types");
function fetchTransactions(paymentKeys, pageNo, blockHeight, currency) {
    return __awaiter(this, void 0, void 0, function* () {
        const res = yield (0, network_1.default)({
            method: "POST",
            url: (0, logic_1.isTestnet)(currency)
                ? `${constants_1.CARDANO_TESTNET_API_ENDPOINT}/v1/transaction`
                : `${constants_1.CARDANO_API_ENDPOINT}/v1/transaction`,
            data: {
                paymentKeys,
                pageNo,
                blockHeight,
            },
        });
        return res.data;
    });
}
function getAllTransactionsByKeys(paymentKeys, blockHeight, currency) {
    return __awaiter(this, void 0, void 0, function* () {
        const transactions = [];
        let latestBlockHeight = 0;
        let isAllTrxFetched = false;
        let pageNo = 1;
        while (!isAllTrxFetched) {
            const res = yield fetchTransactions(paymentKeys, pageNo, blockHeight, currency);
            transactions.push(...res.transactions);
            latestBlockHeight = Math.max(res.blockHeight, latestBlockHeight);
            isAllTrxFetched = res.transactions.length < res.limit;
            pageNo += 1;
        }
        return {
            transactions,
            blockHeight: latestBlockHeight,
        };
    });
}
function getSyncedTransactionsByChain(accountPubKey, accountIndex, chainType, blockHeight, initialPaymentCredentials, currency) {
    return __awaiter(this, void 0, void 0, function* () {
        const keyChainRange = (0, env_1.getEnv)("KEYCHAIN_OBSERVABLE_RANGE") || 20;
        // credentialsMap for efficient use
        const initialPaymentCredentialMap = {};
        let maxUsedKeyIndex = -1;
        initialPaymentCredentials.forEach(cred => {
            initialPaymentCredentialMap[cred.key] = cred;
            if (cred.isUsed)
                maxUsedKeyIndex = cred.path.index;
        });
        const transactions = [];
        let latestBlockHeight = 0;
        // fetch transactions for existing keys
        const trxsRes = yield Promise.all((0, chunk_1.default)(Object.keys(initialPaymentCredentialMap), keyChainRange).map(keys => getAllTransactionsByKeys(keys, blockHeight, currency)));
        trxsRes.forEach(txRes => {
            transactions.push(...txRes.transactions);
            latestBlockHeight = Math.max(latestBlockHeight, txRes.blockHeight);
        });
        // fetch transactions for new avaialble keys
        let newPaymentCredentialsMap = {};
        let lastSyncedKeyIndex = initialPaymentCredentials.length
            ? initialPaymentCredentials[initialPaymentCredentials.length - 1].path.index
            : -1;
        let syncToKeyIndex = maxUsedKeyIndex + keyChainRange;
        while (syncToKeyIndex !== lastSyncedKeyIndex) {
            const currentPaymentKeysMap = {};
            (0, range_1.default)(lastSyncedKeyIndex + 1, syncToKeyIndex + 1, 1).forEach(keyIndex => {
                const keyPath = (0, logic_1.getCredentialKey)(accountPubKey, (0, logic_1.getBipPath)({
                    account: accountIndex,
                    chain: chainType,
                    index: keyIndex,
                }));
                currentPaymentKeysMap[keyPath.key] = {
                    isUsed: false,
                    key: keyPath.key,
                    path: keyPath.path,
                };
            });
            const trxRes = yield getAllTransactionsByKeys(Object.keys(currentPaymentKeysMap), blockHeight, currency);
            transactions.push(...trxRes.transactions);
            lastSyncedKeyIndex = syncToKeyIndex;
            latestBlockHeight = Math.max(latestBlockHeight, trxRes.blockHeight);
            newPaymentCredentialsMap = Object.assign({}, newPaymentCredentialsMap, currentPaymentKeysMap);
            maxUsedKeyIndex = trxRes.transactions.reduce((maxIndexA, { inputs, outputs }) => [...inputs, ...outputs].reduce((maxIndexB, io) => { var _a; return Math.max(((_a = newPaymentCredentialsMap[io.paymentKey]) === null || _a === void 0 ? void 0 : _a.path.index) || -1, maxIndexB); }, maxIndexA), maxUsedKeyIndex);
            syncToKeyIndex = maxUsedKeyIndex + keyChainRange;
        }
        const availablePaymentCredentialsMap = Object.assign(Object.assign({}, initialPaymentCredentialMap), newPaymentCredentialsMap);
        transactions.forEach(trx => {
            [...trx.inputs, ...trx.outputs].forEach(io => {
                if (availablePaymentCredentialsMap[io.paymentKey]) {
                    availablePaymentCredentialsMap[io.paymentKey].isUsed = true;
                }
            });
        });
        return {
            transactions,
            latestBlockHeight,
            paymentCredentials: Object.values(availablePaymentCredentialsMap).sort((aCred, bCred) => aCred.path.index - bCred.path.index),
        };
    });
}
function getTransactions(xpub, accountIndex, initialAccount, blockHeight, currency) {
    var _a, _b;
    return __awaiter(this, void 0, void 0, function* () {
        const accountPubKey = (0, logic_1.getExtendedPublicKeyFromHex)(xpub);
        const oldExternalCredentials = ((_a = initialAccount === null || initialAccount === void 0 ? void 0 : initialAccount.cardanoResources) === null || _a === void 0 ? void 0 : _a.externalCredentials) || [];
        const oldInternalCredentials = ((_b = initialAccount === null || initialAccount === void 0 ? void 0 : initialAccount.cardanoResources) === null || _b === void 0 ? void 0 : _b.internalCredentials) || [];
        const [{ transactions: externalKeyTransactions, latestBlockHeight: aBlockHeight, paymentCredentials: externalCredentials, }, { transactions: internalKeyTransactions, latestBlockHeight: bBlockHeight, paymentCredentials: internalCredentials, },] = yield Promise.all([
            getSyncedTransactionsByChain(accountPubKey, accountIndex, types_1.PaymentChain.external, blockHeight, oldExternalCredentials, currency),
            getSyncedTransactionsByChain(accountPubKey, accountIndex, types_1.PaymentChain.internal, blockHeight, oldInternalCredentials, currency),
        ]);
        return {
            transactions: [...externalKeyTransactions, ...internalKeyTransactions],
            blockHeight: Math.max(aBlockHeight, bBlockHeight),
            externalCredentials,
            internalCredentials,
        };
    });
}
exports.getTransactions = getTransactions;
//# sourceMappingURL=getTransactions.js.map